import { scaleLinear } from 'd3-scale';
import { area, line } from 'd3-shape';
import { css, html } from 'react-strict-dom';

import { RouterLink, Text } from '../../components';
import { Color } from '../../constants';
import { Model } from '../../models';
import { values } from '../../util';
import * as formatters from './formatters';
import useDimens from './useDimens';

type Props = Readonly<{
  companySlug: string;
  kpi: Model.Kpi.View;
}>;

export default function KpiTile({ companySlug, kpi }: Props) {
  const { slug, title, data } = kpi;
  const { measuredEl, width } = useDimens();

  const formatter =
    (formatters as Record<string, (v: number) => string>)[kpi.formatter] ||
    formatters.standard;

  const dataValues = values(data).filter((v): v is number => v != null);
  if (dataValues.length < 1) return null;

  const first = dataValues[0]!;
  const last = dataValues[dataValues.length - 1]!;
  const diff = first != last ? ((last - first) / first) * 100 : 0;

  const headline = kpi.aggregate ? dataValues.reduce((a, b) => a + b, 0) : last;

  const x = scaleLinear()
    .domain([0, dataValues.length - 1])
    .range([0, width ?? 0]);

  const y = scaleLinear()
    .domain([Math.min(...dataValues), Math.max(...dataValues)])
    .range([70, 4]);

  const lineFn = line<{ index: number; value: number }>()
    .x((d) => x(d.index)!)
    .y((d) => y(d.value)!);

  const areaFn = area<{ index: number; value: number }>()
    .x((d) => x(d.index)!)
    .y0(100)
    .y1((d) => y(d.value)!);

  const keyedData = dataValues.map((d, i) => ({ index: i, value: d }));
  const chartLine = lineFn(keyedData) ?? undefined;
  const chartArea = areaFn(keyedData) ?? undefined;

  return (
    <html.div style={styles.container}>
      <RouterLink
        to={`/company/${companySlug}/bi/kpi/${slug}`}
        style={{ display: 'block' }}
      >
        <html.div style={styles.rowTop}>
          <Text style={styles.title}>{title}</Text>
          {!kpi.aggregate && diff != 0 && (
            <Text style={styles.diffText(diff < 0 ? 'neg' : 'pos')}>
              <Text
                as="em"
                italic
                style={styles.diffIcon(diff < 0 ? 'neg' : 'pos')}
              >
                {diff < 0 ? '↓' : '↑'}
              </Text>
              {diff.toFixed(1)}%
            </Text>
          )}
        </html.div>
        <Text as="p" style={styles.value}>
          {formatter(headline)}
        </Text>
        <html.div ref={measuredEl} style={styles.graphWrap}>
          <svg width="100%" height="100" style={{ display: 'block' }}>
            <path
              d={chartArea}
              fill="rgba(136, 148, 215, 0.2)"
              strokeWidth={0}
            />
            <path
              d={chartLine}
              fill="transparent"
              stroke={Color.primaryVibrant}
              strokeWidth={4}
            />
          </svg>
        </html.div>
      </RouterLink>
    </html.div>
  );
}

const styles = css.create({
  container: {
    width: {
      default: '48%',
      '@media (max-width: 700px)': '100%',
    },
    marginRight: {
      default: '2%',
      '@media (max-width: 700px)': 0,
    },
    marginBottom: 20,
    backgroundColor: '#fff',
    borderRadius: 5,
    boxShadow: '0 2px 5px 0 rgba(51, 51, 79, 0.07)',
    transition: 'all 0.3s',
    boxSizing: 'border-box',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#eee',
    ':hover': {
      boxShadow: '0 2px 15px 0 rgba(51, 51, 79, 0.25)',
      transform: 'translateY(-5px)',
      borderColor: '#5742e455',
    },
  },
  rowTop: {
    paddingTop: 25,
    paddingInline: 30,
    paddingBottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
  },
  value: {
    paddingTop: 0,
    paddingInline: 30,
    paddingBottom: 5,
    fontSize: 32,
    color: '#5742e4',
    fontWeight: '400',
  },
  graphWrap: {
    height: 100,
    overflow: 'hidden',
  },
  diffText: (kind: 'neg' | 'pos') => ({
    fontSize: 20,
    color: kind === 'neg' ? '#e84c85' : '#12c457',
  }),
  diffIcon: (kind: 'neg' | 'pos') => ({
    display: 'inline-block',
    fontStyle: 'normal',
    marginRight: 10,
    height: 22,
    width: 22,
    borderRadius: '50%',
    textAlign: 'center',
    fontSize: 16,
    lineHeight: '19px',
    fontWeight: '600',
    backgroundColor:
      kind === 'neg' ? 'rgba(232, 76, 133, 0.1)' : 'rgba(18, 196, 87, 0.1)',
  }),
});
